/**
 * Column definitions and row-data generator for the CPT / J-code table
 * displayed in the single-order authorization-details view.
 * @module cptCodesTable
 */
import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { IconCellVariant } from "../../../../components/custom-table/tableCell/cellTypes/IconCell";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { JCodeStatusValue } from "../../../../enums/jCodeIssueLabel";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import {
  isExceptionFlowForRadiologyOrg,
  isExceptionFlowForUrologyOrg,
  isFacilityExemptFromDateFromValidation,
  isMedOncOrRadiologyOrg,
} from "../../../../enums/organizationTypes";
import { CptCodeWithDetailsKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { getAuthStatusOfOneJCode } from "../../../../utils/getAuthStatusOfMedicalPaOrder";
import { getFilingInfoRequired } from "../../../../utils/getFilingInfoRequired";
import {
  getFilingInfoDetail,
  getRawFilingInfoValue,
} from "../../../../utils/getFilingInfoValue";
import { checkAuthLetterUploadStatus } from "./utils/checkAuthLetterUploadStatus";
import {
  checkIfAuthNumberFieldHasError,
  checkIfDateIsEarlierThanDateOfService,
  checkIfDateIsInCorrectFormat,
  checkIfDateIsLaterThanDateOfService,
  checkIfJCodeFieldHasError,
  checkIfNumVisitsIsBlank,
  checkIfNumVisitsMatchesRemainingAndCompleted,
  checkIfVisitCompletedIsBlank,
  checkIfVisitsRemainingIsBlank,
} from "./utils/computeErrorStates";
import {
  determineIfTheAuthNumberFieldShouldBeEditable,
  determineIfTheJCodeFieldShouldBeEditable,
  determineIfThePendingNumberFieldShouldBeVisible,
  doesAuthStatusAllowEmptyAuthNumber,
} from "./utils/determineIfTheFieldShouldBeEditable";

/**
 * Returns true when the given auth-status id represents an authorization
 * that is either already on file or was obtained by RISA. Used to gate
 * per-field error validation, which should only run for these states.
 */
const isAuthOnFileOrByRISA = (authStatusId: string | undefined): boolean =>
  authStatusId === MedicalPaOrdersAuthStatus.AuthOnFile ||
  authStatusId === MedicalPaOrdersAuthStatus.AuthByRISA;

/**
 * Width of the Description column. Sunstate urology runs 2% narrower; when
 * the Filing Medium column is hidden (non-MedOnc/Radiology facilities), the
 * freed width is absorbed so the column widths still sum to 100%. The doc-upload
 * column stays at 5% for Sunstate so the extra delete column does not underflow.
 *
 * @param isMedOncOrRadiology - Whether the Filing Medium column is shown
 * @param isSunstateUrology - Whether the org is Sunstate urology
 * @returns Description column width percentage
 */
const getDescriptionColumnWidth = (
  isMedOncOrRadiology: boolean,
  isSunstateUrology: boolean,
): number => {
  if (isMedOncOrRadiology) {
    return isSunstateUrology ? 10 : 12;
  }
  return isSunstateUrology ? 12 : 14;
};

/**
 * Builds column definitions for the CPT / J-code custom table.
 *
 * Implemented as a factory (rather than a module-level constant) so the
 * facility-dependent columns can vary at render time:
 *
 * - The CPT/J-code column label is `"CPT Codes"` for radiology exception-flow
 *   facilities (`category === "radiology"`) and urology exception-flow orgs;
 *   `"J Codes"` everywhere else.
 * - The `Description` column is **mutually exclusive** by facility — radiology
 *   exception-flow facilities get a `SEARCHABLE_CARD` editable cell
 *   (`CptCodesEditableDescription`), all others get the read-only
 *   `STRING_WITH_ICON` description cell (`Description`). Including both columns
 *   (even at `width: 0`) renders an empty `<td>` whose internal control leaks a
 *   sliver of UI, so we pick exactly one based on `isRadiologyExceptionFlow`.
 * - The `Filing Medium` column is only shown for MedOnc/Radiology facilities
 *   (`isMedOncOrRadiology`). When hidden, its 8% width is redistributed across
 *   the date, CPT/J-code, auth-number, description, and visit columns so the
 *   column widths still sum to 100%.
 *
 * Evaluating at call time avoids the prior bug where the label was frozen
 * to whatever `getCurrentFacilityId()` returned at module import (typically
 * `null`, before auth/token initialization completed).
 *
 * @param facilityId - The current facility id, typically obtained from
 *   `getCurrentFacilityId()` at render time. May be `null` when the
 *   facility context has not yet been resolved.
 * @param category - Healthcare facility `category` used for radiology columns
 * @returns Ordered column definitions consumed by the custom table component.
 */
export const getCptCodesTableColumnsKeys = (
  facilityId: string | null,
  category?: string | null,
): TableHeader[] => {
  const isRadiologyExceptionFlow = isExceptionFlowForRadiologyOrg(category);
  const isSunstateUrology = isExceptionFlowForUrologyOrg(facilityId);
  const isMedOncOrRadiology = isMedOncOrRadiologyOrg(category);

  // Radiology exception-flow orgs show an editable searchable-card
  // "Description" column instead of the plain read-only one. Mutually
  // exclusive — including both columns (even at width: 0) still renders a
  // `<td>` whose internal control can leak a sliver of UI on the screen.
  const descriptionColumn: TableHeader = isRadiologyExceptionFlow
    ? {
        label: "Description",
        key: CptCodeWithDetailsKeys.CptCodesEditableDescription,
        order: 4,
        width: getDescriptionColumnWidth(
          isMedOncOrRadiology,
          isSunstateUrology,
        ),
        type: TableCellType.SEARCHABLE_CARD,
      }
    : {
        label: "Description",
        key: CptCodeWithDetailsKeys.Description,
        order: 4,
        width: getDescriptionColumnWidth(
          isMedOncOrRadiology,
          isSunstateUrology,
        ),
        type: TableCellType.STRING_WITH_ICON,
      };

  return [
    {
      label: "Date From",
      key: CptCodeWithDetailsKeys.StartFrom,
      order: 1,
      width: isMedOncOrRadiology ? 9 : 10,
      sortable: true,
      type: TableCellType.EDITABLE_TEXT,
    },
    {
      label: "Date To",
      key: CptCodeWithDetailsKeys.EndAt,
      order: 1,
      width: isMedOncOrRadiology ? 9 : 10,
      sortable: true,
      type: TableCellType.EDITABLE_TEXT,
    },
    {
      label:
        isRadiologyExceptionFlow || isSunstateUrology ? "CPT Codes" : "J Codes",
      key: CptCodeWithDetailsKeys.JCodes,
      order: 3,
      width: isMedOncOrRadiology ? 8 : 9,
      type: TableCellType.TEXT_CELL_WITH_INPUT,
    },
    {
      label: "Auth/Denial #",
      key: CptCodeWithDetailsKeys.AuthorizationNumber,
      order: 4,
      width: isMedOncOrRadiology ? 8 : 9,
      type: TableCellType.TEXT_CELL_WITH_INPUT,
    },
    {
      label: "Pending#",
      key: CptCodeWithDetailsKeys.PendingNumber,
      order: 3,
      width: 7,
      type: TableCellType.TEXT_CELL_WITH_INPUT,
    },
    descriptionColumn,
    {
      label: "Total Visits",
      key: CptCodeWithDetailsKeys.Visits,
      order: 5,
      width: isMedOncOrRadiology ? 8 : 9,
      type: TableCellType.EDITABLE_TEXT,
    },
    {
      label: "Remaining",
      key: CptCodeWithDetailsKeys.RemainingVisits,
      order: 6,
      width: isMedOncOrRadiology ? 8 : 9,
      type: TableCellType.EDITABLE_TEXT,
    },
    {
      label: "Completed",
      key: CptCodeWithDetailsKeys.VisitsAllowed,
      order: 7,
      width: 8,
      type: TableCellType.EDITABLE_TEXT,
    },
    {
      label: "Auth Required",
      key: CptCodeWithDetailsKeys.PaRequired,
      order: 9,
      width: isSunstateUrology ? 9 : 10,
      type: TableCellType.SELECTABLE_BADGE,
    },
    ...(isMedOncOrRadiology
      ? [
          {
            label: "Filing Medium",
            key: CptCodeWithDetailsKeys.FilingInfo,
            order: 8,
            width: 8,
            type: TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK,
          },
        ]
      : []),
    {
      label: "",
      key: CptCodeWithDetailsKeys.DocWithUploadIcon,
      order: 10,
      width: 5,
      type: TableCellType.IS_DOCUMENT_UPLOADED,
    },
    ...(isSunstateUrology
      ? [
          {
            label: "",
            key: CptCodeWithDetailsKeys.DeleteIcon,
            order: 11,
            width: 3,
            type: TableCellType.ICON,
          },
        ]
      : []),
  ];
};

/**
 * Transforms raw auth-entry J-codes into table-row objects consumed
 * by the custom table component, computing error states and display values.
 *
 * @param generalOrderDetails - Unwrapped Postgres `.order` payload; supplies
 *   the patient context and date-of-service used for per-cell validation.
 * @param authOnFileDetails - Auth-on-file payload supplying the J-code entries.
 * @param authStatusOptions - Available auth-status options for badge rendering.
 * @param options - Options surfaced inside each SEARCHABLE_CARD description
 *   cell. Defaults to `[]` when omitted so the cell still renders without
 *   crashing the dropdown.
 * @param category - Healthcare facility `category` driving the start-date
 *   validation exemption.
 * @returns Array of row objects keyed by {@link CptCodeWithDetailsKeys}.
 */
export const generateCptCodesTableData = (
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile,
  authStatusOptions: AuthStatusOptionModel[],
  options: SearchableCardOption[] = [],
  category?: string | null,
) => {
  const authEntries =
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries ?? [];
  const transformedDateOfService = formatDateToUTCWithFormat(
    generalOrderDetails?.payload?.date_of_service ?? "",
    "MM/DD/YYYY",
    moment.utc().format("MM/DD/YYYY"),
  );
  const getAllJCodesData = () => {
    let JcodesData: JCodeDetails[] = [];
    authEntries.forEach((item) => {
      JcodesData.push(...(item?.j_codes ?? []));
    });
    return JcodesData;
  };
  const JcodesData = getAllJCodesData();
  return JcodesData.map((item, index) => {
    const authStatus = getAuthStatusOfOneJCode(item, authStatusOptions);

    const description = () => {
      if (item?.medicine_name && item?.medicine_name !== "") {
        return item?.medicine_name;
      }
      return item?.description ?? "N/A";
    };

    const shouldShowDateFromError =
      isAuthOnFileOrByRISA(authStatus?.id) &&
      !isFacilityExemptFromDateFromValidation(category);

    return {
      [CptCodeWithDetailsKeys.StartFrom]: {
        data: item?.start_date
          ? moment(item.start_date).format("MM/DD/YYYY")
          : "",
        placeholder: "MM/DD/YYYY",
        hasError: shouldShowDateFromError
          ? !checkIfDateIsInCorrectFormat(
              item?.start_date ?? "",
              "MM/DD/YYYY",
            ) ||
            !checkIfDateIsEarlierThanDateOfService(
              item?.start_date ?? "",
              transformedDateOfService ?? "",
            )
          : false,
      },
      [CptCodeWithDetailsKeys.EndAt]: {
        data: item?.end_date ? moment(item.end_date).format("MM/DD/YYYY") : "",
        placeholder: "MM/DD/YYYY",
        hasError: isAuthOnFileOrByRISA(authStatus?.id)
          ? !checkIfDateIsInCorrectFormat(item?.end_date ?? "", "MM/DD/YYYY") ||
            !checkIfDateIsLaterThanDateOfService(
              item?.end_date ?? "",
              transformedDateOfService ?? "",
            )
          : false,
      },
      [CptCodeWithDetailsKeys.Description]: {
        text: description(),
        shouldShowAlertIcon:
          item?.drug_preference === JCodeStatusValue.NON_PREFERRED,
        shouldShowBlockedIcon: item?.nccn_match === JCodeStatusValue.NO_MATCH,
        textWidth: "80%",
      },
      [CptCodeWithDetailsKeys.PaRequired]: {
        text: authStatus?.text,
        textColor: authStatus?.textColor,
        bgColor: authStatus?.bgColor,
        currentStatusId: authStatus?.id,
        drug_name: generalOrderDetails?.payload?.regimen_name ?? "",
      },
      // `value` holds the filing mode; the badge label is derived at render
      // time via `getFilingInfoValue`. `priorAuthStatusId` is set later by
      // `recomputeFilingInfo` when PA Required changes. `isStatic` is false
      // only for `no_auth_required` on initial load; PA transitions use
      // `shouldTheFilingInfoBeStatic` in the reducer.
      [CptCodeWithDetailsKeys.FilingInfo]: {
        id: item?.id ?? "",
        value: getRawFilingInfoValue(authStatus?.id, item?.filing_mode ?? ""),
        detail: getFilingInfoDetail(authStatus?.id, item?.filing_detail ?? ""),
        isRequired: getFilingInfoRequired(authStatus?.id, authStatus?.id),
        isStatic: authStatus?.id !== MedicalPaOrdersAuthStatus.NoAuthRequired,
        hasError: false,
      },
      [CptCodeWithDetailsKeys.Visits]: {
        data: item?.num_visits ?? "",
        placeholder: "0",
        hasError: isAuthOnFileOrByRISA(authStatus?.id)
          ? checkIfNumVisitsIsBlank(item?.num_visits) ||
            !checkIfNumVisitsMatchesRemainingAndCompleted(
              item?.num_visits,
              item?.visits_remaining,
              item?.visits_happened,
            )
          : false,
      },
      [CptCodeWithDetailsKeys.RemainingVisits]: {
        data: item?.visits_remaining ?? "",
        placeholder: "0",
        hasError: isAuthOnFileOrByRISA(authStatus?.id)
          ? checkIfVisitsRemainingIsBlank(item?.visits_remaining)
          : false,
      },
      [CptCodeWithDetailsKeys.VisitsAllowed]: {
        data: item?.visits_happened ?? "",
        placeholder: "0",
        hasError: isAuthOnFileOrByRISA(authStatus?.id)
          ? checkIfVisitCompletedIsBlank(item?.visits_happened)
          : false,
      },
      [CptCodeWithDetailsKeys.CptCodesEditableDescription]: {
        options: options ?? [],
        placeholder: "Enter CPT codes description",
        value: description() ?? "",
        id: item?.id ?? "",
      },
      [CptCodeWithDetailsKeys.JCodes]: {
        data: item?.j_code ?? "",
        id: item?.id ?? "",
        allowInputModification: determineIfTheJCodeFieldShouldBeEditable(
          authStatus?.id,
          item?.j_code ?? "",
        ),
        hasError: checkIfJCodeFieldHasError(authStatus?.id, item?.j_code ?? ""),
        type: CptCodeWithDetailsKeys.JCodes,
      },
      [CptCodeWithDetailsKeys.AuthorizationNumber]: {
        data: item?.auth_number ?? "",
        id: item?.id ?? "",
        allowInputModification: determineIfTheAuthNumberFieldShouldBeEditable(
          authStatus?.id,
        ),
        type: CptCodeWithDetailsKeys.AuthorizationNumber,
        hasError: checkIfAuthNumberFieldHasError(
          item?.auth_number ?? "",
          authStatus?.id,
        ),
        allowEmptyValue: doesAuthStatusAllowEmptyAuthNumber(authStatus?.id),
      },
      [CptCodeWithDetailsKeys.PendingNumber]: {
        data: item?.pending_number ?? "",
        id: item?.id ?? "",
        allowInputModification: determineIfThePendingNumberFieldShouldBeVisible(
          authStatus?.id,
        ),
        type: CptCodeWithDetailsKeys.PendingNumber,
        hasError: false,
        allowEmptyValue: true,
      },
      [CptCodeWithDetailsKeys.JCodeID]: item?.id ?? "N/A",
      [CptCodeWithDetailsKeys.DocWithUploadIcon]: {
        isUploaded: checkAuthLetterUploadStatus(
          authStatus?.id,
          item?.documents ?? [],
        ),
        isVisible: item?.auth_status === MedicalPaOrdersAuthStatus.AuthOnFile,
        orderId: generalOrderDetails?.order_id ?? "N/A",
        jCode: item?.j_code ?? "N/A",
        jCodeId: item?.id ?? "N/A",
        authStatus: item?.auth_status ?? "N/A",
        authLetterReviewStatus: item?.auth_letter_review_status,
      },
      [CptCodeWithDetailsKeys.DeleteIcon]: {
        variant: IconCellVariant.DELETE,
        rowData: item,
      },
    };
  });
};
